import React, { useEffect, useMemo, useState } from "react";

import { loadLaunchpadCandles } from "../../../services/strategyDeployApi";
import type { StrategyRdOhlcBar } from "../../../types/strategyRd";
import type { DeploySignal } from "../../../types/strategyDeploy";

/** Lab chart palette: grey candles, red risk / green reward boxes, surf entry. */
const CANDLE_UP = "#8E97A6";
const CANDLE_DOWN = "#374151";
const ENTRY = "#7DD3E8";
const RISK_FILL = "rgba(224, 1, 1, 0.12)";
const REWARD_FILL = "rgba(6, 227, 99, 0.12)";
const BARS_BEFORE = 48;

type Props = {
  signal: DeploySignal;
  candleType: string;
  height?: number;
  /** Shared across cards of one deployment so the candles load once. */
  candles?: StrategyRdOhlcBar[] | null;
};

const cache = new Map<string, Promise<StrategyRdOhlcBar[]>>();

export function loadCandles(symbol: string, candleType: string): Promise<StrategyRdOhlcBar[]> {
  const minute = Math.floor(Date.now() / 60_000);
  const key = `${symbol}|${candleType}|${minute}`;
  if (!cache.has(key)) {
    const days = /^(\d+)m$/.test(candleType) && Number(candleType.slice(0, -1)) <= 15 ? 3 : 20;
    cache.set(
      key,
      loadLaunchpadCandles(symbol, candleType, days).catch(() => [])
    );
  }
  return cache.get(key)!;
}

/** The signal on its candles: entry, stop and target drawn from the entry candle to the exit
 *  (or to now while the trade is open). */
export function SignalChart({ signal, candleType, height = 168, candles: given }: Props): JSX.Element {
  const [bars, setBars] = useState<StrategyRdOhlcBar[] | null>(given ?? null);
  useEffect(() => {
    if (given) {
      setBars(given);
      return;
    }
    let live = true;
    loadCandles(signal.symbol, candleType).then((c) => live && setBars(c));
    return () => {
      live = false;
    };
  }, [given, signal.symbol, candleType]);

  const view = useMemo(() => {
    if (!bars?.length) return null;
    const entryT = new Date(signal.entry_bar_time || signal.created_at).getTime();
    const exitT = signal.exit_time ? new Date(signal.exit_time).getTime() : null;
    let entryIdx = bars.findIndex((b) => new Date(b.timestamp).getTime() >= entryT);
    if (entryIdx < 0) entryIdx = bars.length - 1;
    let exitIdx = exitT != null ? bars.findIndex((b) => new Date(b.timestamp).getTime() >= exitT) : -1;
    if (exitIdx < 0) exitIdx = bars.length - 1;
    const start = Math.max(0, entryIdx - BARS_BEFORE);
    const end = Math.min(bars.length, Math.max(exitIdx + 12, entryIdx + 24));
    const win = bars.slice(start, end);
    const prices = win.flatMap((b) => [b.high, b.low]);
    for (const p of [signal.entry_price, signal.stop_price, signal.target_price]) {
      if (p != null && Number.isFinite(p)) prices.push(p);
    }
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    // Still open: leave room on the right so the stop / target boxes reach into the future.
    const open = exitT == null;
    const future = open ? Math.max(10, Math.round(win.length * 0.22)) : 0;
    return { win, start, entryIdx: entryIdx - start, exitIdx: exitIdx - start, min, max, future, open };
  }, [bars, signal]);

  if (!view) {
    return (
      <div
        className="flex w-full items-center justify-center rounded-[6px] bg-muted/40 text-xs text-muted-foreground"
        style={{ height }}
      >
        {bars ? "No candles yet" : "Loading chart…"}
      </div>
    );
  }

  const W = 600;
  const H = height;
  const pad = 6;
  const n = view.win.length;
  const step = (W - pad * 2) / Math.max(n + view.future, 1);
  const span = Math.max(view.max - view.min, 1e-9);
  const y = (p: number) => pad + (H - pad * 2) * (1 - (p - view.min) / span);
  const x = (i: number) => pad + step * i + step / 2;
  const bodyW = Math.max(1, step * 0.62);
  const { entry_price: e, stop_price: s, target_price: t } = signal;
  const x0 = x(view.entryIdx) - step / 2;
  const x1 = view.open ? W - pad : x(view.exitIdx) + step / 2;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="block w-full rounded-[6px] bg-muted/30"
      style={{ height }}
      role="img"
      aria-label={`${signal.direction} ${signal.symbol} signal chart`}
    >
      {e != null && s != null ? (
        <rect x={x0} width={x1 - x0} y={Math.min(y(e), y(s))} height={Math.abs(y(e) - y(s))} fill={RISK_FILL} />
      ) : null}
      {e != null && t != null ? (
        <rect x={x0} width={x1 - x0} y={Math.min(y(e), y(t))} height={Math.abs(y(e) - y(t))} fill={REWARD_FILL} />
      ) : null}
      {view.win.map((b, i) => {
        const up = b.close >= b.open;
        const color = up ? CANDLE_UP : CANDLE_DOWN;
        const top = y(Math.max(b.open, b.close));
        const bot = y(Math.min(b.open, b.close));
        return (
          <g key={b.timestamp}>
            <line x1={x(i)} x2={x(i)} y1={y(b.high)} y2={y(b.low)} stroke={color} strokeWidth={1} />
            <rect x={x(i) - bodyW / 2} width={bodyW} y={top} height={Math.max(1, bot - top)} fill={color} />
          </g>
        );
      })}
      {e != null ? (
        <line x1={x0} x2={x1} y1={y(e)} y2={y(e)} stroke={ENTRY} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
      ) : null}
      <line
        x1={x(view.entryIdx)}
        x2={x(view.entryIdx)}
        y1={pad}
        y2={H - pad}
        stroke={ENTRY}
        strokeDasharray="3 4"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
        opacity={0.6}
      />
    </svg>
  );
}
